import { start } from '@popperjs/core';
import { getCSRFToken } from '../utils/getCSRFToken';
import { postJSONWithCSRFRetry } from '../utils/fetchWithCSRFRetry';
import {
  formatDateISO,
  parseDate,
  calculateDaysInclusive,
  calculateNights,
} from '../utils/dateUtils';

// Polyfill for fetch timeout using Promise.race
function fetchWithTimeout(resource, options = {}, timeout = 30000) {
  return Promise.race([
    fetch(resource, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timed out')), timeout),
    ),
  ]);
}

export default function registerStoreBookingForm() {
  // CRITICAL FIX: Get session from storeCartStore to prevent race conditions
  // The storeCartStore is the single source of truth for session management
  // DO NOT create new sessions here - it causes bookings to be lost!

  // Helper function to safely get session ID
  const getSessionId = () => {
    // First try to get from storeCartStore if available
    const cartStore = window.Alpine?.store('storeCartStore');
    if (cartStore && cartStore.getSessionId) {
      return cartStore.getSessionId();
    }

    // Fallback: check storage locations but DO NOT create new session
    return (
      sessionStorage.getItem('bookingSessionId') ||
      localStorage.getItem('bookingSessionId')
    );
  };

  // ADD BOOKING ITEM STORE
  window.Alpine.store('storeBookingForm', {
    // Use getter to always fetch current session instead of storing stale value
    get sessionId() {
      return getSessionId();
    },
    startDate: '',
    endDate: '',
    selectedMonth: null, // Currently selected month on FLatpickr
    fixedDateRange: '',
    autoSelectFixedDateRange: false,
    bookingCutOff: 0,
    minDate: null,
    minMaxDays: {},
    closedToDays: {},
    unavailableDates: [], // format should be ['23-01-2024']
    product: 'Accommodation',
    soldAs: null,
    soldAsFixed: null,
    dateSelectType: 'range',
    //soldAsData: null,
    unitId: null,
    totalUnits: 1,
    units: 1,
    isSubmitting: false,

    peopleAsOption: false,
    peoplePerUnit: 0, // Store the people per unit from RateSet
    amountPeopleOptions: [], // Store the generated options for amount_people
    selectedPeople: null, // Default selected option

    price: 0,
    currency: '',
    priceUpdated: false,
    // Discount information
    discountAmount: 0,
    appliedDiscounts: [],
    flatpickrInstances: {},
    categorizedDates: {
      start: [],
      single: [],
      middle: [],
      end: [],
    },
    packages: [], // Package info for dynamic availability checking
    packageMaxPeople: null, // Max people limit for packages (from secondary component constraints)
    packageMinPeople: null, // Min people required for packages (from FIXED component constraints)
    // Add new person type related properties
    usePersonTypes: false,
    unitType: null,
    personType: '',
    selectedAdults: 0,
    selectedChildren: 0,
    adultOptions: [],
    childrenOptions: [],
    maxPersons: 0,

    // Custom dropdown state
    customDropdownOpen: false,
    customDropdownOptions: [],
    customDropdownSelected: null,

    // Tour duration selection
    tourDuration: 'single', // Default to single day for tours
    showTourDurationToggle: false, // Only show when rate set allows range
    tourDurationMode: 'both_single_default', // Options: both_single_default, both_multi_default, single_only, multi_only
    pendingStartDateForModeSwitch: null, // Holds date when auto-switching from single to multi-day mode

    // Preset durations (overrides tour duration mode when set)
    presetDurations: [], // Array of {id, duration, name, order, is_default}
    selectedPresetDuration: null, // Currently selected preset duration (number of nights/days)
    hasPresetDurations: false, // Quick check if preset mode is active
    presetDateJustSelected: false, // Flag to prevent async fetch from resetting display

    // Validation state
    validationErrors: {
      peopleUnits: '',
    },
    validationTimer: null,
    lastValidationTime: 0, // Guard against double-firing from touchstart + click

    // Submit message state
    submitMessage: '',
    messageType: '',
    submitMessageTimer: null,

    // Form change tracking
    hasUnsavedChanges: false,

    // New flow state
    showViewCartButton: false,

    // Custom fields state
    selectedRateSet: null, // Store the full rate set object including custom_field_definitions
    customFields: {}, // Track per-booking-item custom field values
    customFieldsPerPerson: [], // Track per-person custom field values

    // Custom field help modal state
    showCustomFieldHelpModal: false,
    selectedCustomFieldForHelp: null,

    getPersonLabel(count = 2, lowercase = false) {
      const singular = (
        this.selectedRateSet?.person_label_singular || 'Person'
      ).trim();
      const plural = (
        this.selectedRateSet?.person_label_plural || 'People'
      ).trim();
      const label = Number(count) === 1 ? singular : plural;
      return lowercase ? label.toLowerCase() : label;
    },
    getPersonFieldLabel() {
      return this.getPersonLabel(2);
    },
    showPersonIcon() {
      const rs = this.selectedRateSet;
      if (!rs) return true;
      const raw = rs.person_label_singular;
      if (raw == null || raw === '') return true;
      const s = String(raw).trim();
      return !s || s.toLowerCase() === 'person';
    },

    // Method to validate people vs units
    validatePeopleUnits() {
      console.log('FUNCTION CALLED: validatePeopleUnits');

      let totalPeople = 0;

      // Calculate total people based on configuration
      if (this.usePersonTypes) {
        totalPeople =
          (parseInt(this.selectedAdults) || 0) +
          (parseInt(this.selectedChildren) || 0);
      } else if (this.peopleAsOption) {
        totalPeople = parseInt(this.selectedPeople) || 0;
      } else if (
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child'
      ) {
        // For Person-type units without peopleAsOption, units = people
        totalPeople = parseInt(this.units) || 0;
      }

      console.log(
        'Validating - totalPeople:',
        totalPeople,
        'units:',
        this.units,
        'peopleAsOption:',
        this.peopleAsOption,
        'usePersonTypes:',
        this.usePersonTypes,
        'unitType:',
        this.unitType,
      );

      // 📦 Check package minimum people requirement FIRST (works for all cases)
      console.log('📦 Checking package min people:', {
        packageMinPeople: this.packageMinPeople,
        totalPeople: totalPeople,
        willShowWarning:
          this.packageMinPeople && totalPeople < this.packageMinPeople,
      });

      if (
        this.packageMinPeople &&
        totalPeople > 0 &&
        totalPeople < this.packageMinPeople
      ) {
        const message = `This package requires at least ${
          this.packageMinPeople
        } ${this.getPersonLabel(
          this.packageMinPeople,
          true,
        )} total. Please increase the number of adults and/or children.`;

        console.log('📦 Setting package minimum validation error:', message);
        this.setValidationError('peopleUnits', message);
        return false;
      }

      // Skip remaining validation if people are not being selected separately from units
      if (!this.peopleAsOption) {
        console.log('Skipping remaining validation - peopleAsOption is false');
        this.clearAllValidationErrors();
        return true;
      }

      if (totalPeople < this.units) {
        // Get unit type name
        const unitTypeName = this.unitType || 'unit';
        const message = `Please select at least ${
          this.units
        } ${this.getPersonLabel(
          this.units,
          true,
        )} (minimum 1 ${this.getPersonLabel(1, true)} per ${unitTypeName}).`;

        // Use general validation error method with auto-dismiss
        this.setValidationError('peopleUnits', message);
        return false;
      }

      // Clear all validation errors immediately when validation passes
      this.clearAllValidationErrors();
      return true;
    },
    // Hides the units field if there is only 1 unit available
    showUnitsField() {
      console.log(
        'showUnitsField called, units:',
        this.totalUnits,
        typeof this.totalUnits,
      );
      return Number(this.totalUnits) > 1;
    },

    // Method to set validation error with auto-dismiss
    setValidationError(field, message) {
      console.log(`❌ Setting validation error for ${field}:`, message);

      // Clear any existing timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
        this.validationTimer = null;
      }

      // Set the error
      this.validationErrors = {
        ...this.validationErrors,
        [field]: message,
      };
      // No timer: all warnings persist until validation passes
    },

    // Method to clear all validation errors
    clearAllValidationErrors() {
      console.log('🧹 Clearing all validation errors');

      // Clear timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
        this.validationTimer = null;
      }

      // Clear all errors by creating new object (forces Alpine reactivity)
      this.validationErrors = {
        peopleUnits: '',
        // Add other validation fields here as needed
      };

      console.log('✅ All validation errors cleared:', this.validationErrors);
    },

    // Method to set submit message with auto-dismiss
    setSubmitMessage(message, type = 'error') {
      console.log(`📢 Setting submit message (${type}):`, message);

      // Clear any existing timer
      if (this.submitMessageTimer) {
        clearTimeout(this.submitMessageTimer);
      }

      // Set the message
      this.submitMessage = message;
      this.messageType = type;

      // Show overlay for success messages
      if (type === 'success') {
        this.showSuccessOverlay = true;
      } else {
        this.showSuccessOverlay = false;
      }

      // Auto-clear after 4 seconds (only for error messages)
      if (type === 'error') {
        this.submitMessageTimer = setTimeout(() => {
          console.log('🕐 Submit message timer fired - clearing message');
          this.clearSubmitMessage();
        }, 4000);
      }
    },

    // Method to clear submit message
    clearSubmitMessage() {
      console.log('🧹 Clearing submit message');

      // Clear timer
      if (this.submitMessageTimer) {
        clearTimeout(this.submitMessageTimer);
        this.submitMessageTimer = null;
      }

      // Clear message
      this.submitMessage = '';
      this.messageType = '';
      this.showSuccessOverlay = false;

      console.log('✅ Submit message cleared');
    },

    // Method to handle "Continue adding..." button click
    continueAdding() {
      console.log('🔄 Continue adding clicked');
      this.showSuccessOverlay = false;
      this.submitMessage = '';
      this.messageType = '';
      window.dispatchEvent(
        new CustomEvent('store-booking:continue-adding', {
          detail: {
            soldAs: this.soldAs,
          },
        }),
      );
    },

    // Method to check if form is valid for submission
    isFormValid() {
      return this.validatePeopleUnits() && this.startDate && this.endDate;
    },

    // Method to check if user has made any changes to the form
    hasFormChanges() {
      return this.hasUnsavedChanges;
    },

    // Mark that the form has unsaved changes
    markFormChanged() {
      this.hasUnsavedChanges = true;
    },

    // Clear the unsaved changes flag
    clearFormChanges() {
      this.hasUnsavedChanges = false;
    },

    formattedPrice() {
      // Calculate total price including extras
      let totalPrice = parseFloat(this.price) || 0; // Convert string to number

      // Add extras prices from storeRateSetStore
      const storeRateSetStore = Alpine.store('storeRateSetStore');
      if (storeRateSetStore && storeRateSetStore.hasSelectedExtras()) {
        const selectedExtras = storeRateSetStore.getSelectedExtras();
        const extrasTotal = selectedExtras.reduce((total, selectedExtra) => {
          const extra = selectedExtra.extra;
          let extraPrice = parseFloat(extra.unit_price);

          // Apply per-day pricing calculation if needed
          if (extra.pricing_basis === 'per_day' && this.startDate) {
            const startDate = parseDate(this.startDate);
            const endDate = parseDate(this.endDate || this.startDate); // Use startDate as fallback for single-day tours

            console.log('🔍 [storeBookingForm] Subtotal Calculation Debug:');
            console.log('  Extra:', extra.name);
            console.log('  Raw startDate:', this.startDate);
            console.log('  Raw endDate:', this.endDate);
            console.log('  Parsed startDate:', startDate);
            console.log('  Parsed endDate:', endDate);

            if (
              startDate &&
              endDate &&
              !isNaN(startDate.getTime()) &&
              !isNaN(endDate.getTime())
            ) {
              // Calculate days more reliably by counting actual days
              const startDateOnly = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate(),
              );
              const endDateOnly = new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate(),
              );

              let daysDiff = 0;
              const currentDate = new Date(startDateOnly);
              while (currentDate < endDateOnly) {
                daysDiff++;
                currentDate.setDate(currentDate.getDate() + 1);
              }

              console.log('  Start date:', startDateOnly.toDateString());
              console.log('  End date:', endDateOnly.toDateString());
              console.log('  Days difference (calculated):', daysDiff);

              // Handle date calculation based on accommodation vs tour
              // Need to determine if this is accommodation or tour from the current store
              const currentStore = window.Alpine.store('storeRateSetStore');
              const isAccommodation =
                currentStore &&
                currentStore.rateSet &&
                currentStore.rateSet.rate_set_type === 'accommodation';

              if (daysDiff === 0) {
                daysDiff = 1; // Single day selection
                console.log('  Single day selection: set to 1 day');
              } else if (isAccommodation) {
                // For accommodations: 28th-30th = 2 days difference = 2 nights (use as-is)
                console.log(
                  '  Accommodation: using duration as nights (no adjustment)',
                );
              } else {
                // For tours: 15th-17th = 2 days difference = 3 days inclusive (add 1)
                daysDiff += 1;
                console.log('  Tour: added 1 for inclusive range');
              }

              if (daysDiff > 0) {
                // Apply duration adjustment if the extra has one
                let adjustedDaysDiff = daysDiff;
                if (extra.duration_adjustment) {
                  adjustedDaysDiff = Math.max(
                    1,
                    daysDiff + parseInt(extra.duration_adjustment),
                  );
                  console.log(
                    '  Duration adjustment:',
                    extra.duration_adjustment,
                  );
                  console.log('  Adjusted daysDiff:', adjustedDaysDiff);
                }

                extraPrice = parseFloat(extra.unit_price) * adjustedDaysDiff;
                console.log('  Final daysDiff:', adjustedDaysDiff);
                console.log('  Unit price:', extra.unit_price);
                console.log('  Calculated extraPrice:', extraPrice);
              }
            }
          }

          return total + extraPrice * selectedExtra.quantity;
        }, 0);
        totalPrice += extrasTotal;
      }

      const safeCurrency =
        typeof this.currency === 'string' && this.currency.trim().length === 3
          ? this.currency.trim().toUpperCase()
          : 'USD';

      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: safeCurrency,
        }).format(totalPrice);
      } catch (error) {
        console.warn(
          'Invalid currency for formattedPrice:',
          this.currency,
          error,
        );
        return `${totalPrice.toFixed(2)}`;
      }
    },
    getEffectiveDateSelectionMode() {
      // Preset durations always begin with a single selected date.
      if (this.hasPresetDurations) {
        return 'single';
      }

      // Tour duration mode can override generic range behavior.
      if (
        this.product === 'Tour' &&
        this.dateSelectType === 'range' &&
        !this.soldAsFixed
      ) {
        if (this.tourDurationMode === 'single_only') {
          return 'single';
        }

        if (this.tourDurationMode === 'multi_only') {
          return 'range';
        }

        if (this.showTourDurationToggle) {
          return this.tourDuration === 'single' ? 'single' : 'range';
        }
      }

      return this.dateSelectType;
    },
    initializeFlatpickr(product, flatpickrElement, minDate) {
      console.log('Session ID:', this.sessionId);
      console.log('minDate:', this.minDate);

      // Determine mode from effective selection constraints.
      const mode = this.getEffectiveDateSelectionMode();

      if (this.hasPresetDurations) {
        console.log('📅 Preset duration mode: forcing single date selection');
      }

      const placeholder = this.setPlaceholder();
      this.flatpickrInstances[product] = flatpickr(flatpickrElement, {
        disableMobile: 'true',
        mode: mode,
        altInput: true,
        altFormat: 'j M Y',
        dateFormat: 'd/m/Y',
        showMonths:
          mode === 'range'
            ? window.matchMedia('(min-width: 769px)').matches
              ? 2
              : 1
            : 1, // Show two months on desktop, one on mobile for range selection
        minDate: new Date(minDate), // Based on Booking Cut Off days set for RateSet

        onReady: function (selectedDates, dateStr, instance) {
          instance.altInput.setAttribute('placeholder', placeholder);
        },
        onChange: (selectedDates, dateStr, instance) => {
          // Clear success message when user starts new entry
          this.clearSubmitMessage();

          // 📅 PRESET DURATION MODE: Auto-calculate end date
          if (
            this.hasPresetDurations &&
            this.selectedPresetDuration &&
            selectedDates.length === 1
          ) {
            const selectedDate = selectedDates[0];
            this.startDate = formatDateISO(selectedDate);

            // Calculate end date based on preset duration
            // For accommodations: end date is checkout day (start + duration)
            // For tours: end date is last day of tour (start + duration - 1)
            const endDate = new Date(selectedDate);
            if (this.product === 'Accommodation') {
              endDate.setDate(endDate.getDate() + this.selectedPresetDuration);
            } else {
              endDate.setDate(
                endDate.getDate() + this.selectedPresetDuration - 1,
              );
            }
            this.endDate = formatDateISO(endDate);

            console.log(
              `📅 Preset duration: ${this.selectedPresetDuration} → Start: ${this.startDate}, End: ${this.endDate}`,
            );

            console.log(
              '📅 PRESET: Setting dates',
              this.startDate,
              'to',
              this.endDate,
            );

            // Set flag to prevent async fetch from resetting display
            this.presetDateJustSelected = true;

            // Update the display to show the full date range
            const formatDisplayDate = (date) => {
              const options = {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              };
              return date.toLocaleDateString('en-US', options);
            };
            const displayText = `${formatDisplayDate(
              selectedDate,
            )} to ${formatDisplayDate(endDate)}`;
            if (instance.altInput) {
              instance.altInput.value = displayText;
            }

            // Close the picker
            instance.close();

            // Update price immediately
            this.updatePrice();
            this.markFormChanged();
            this.clearValidationError('startEndDate');
            return; // Exit early - no need for further processing
          }

          // Check if we're in single mode and need to auto-switch to multi-day
          // (when the selected date has min_days > 1)
          // Skip this check in preset duration mode
          if (
            !this.hasPresetDurations &&
            mode === 'single' &&
            selectedDates.length === 1
          ) {
            const selectedDate = selectedDates[0];
            const day = selectedDate.getDate().toString().padStart(2, '0');
            const month = (selectedDate.getMonth() + 1)
              .toString()
              .padStart(2, '0');
            const year = selectedDate.getFullYear();
            const dateKey = `${day}-${month}-${year}`;
            const minDays = this.minMaxDays[dateKey]?.min_days;

            if (minDays && minDays > 1) {
              console.log(
                `📅 Date ${dateKey} requires min ${minDays} days, switching to multi-day mode`,
              );
              this.switchToMultiDayWithDate(selectedDate);
              return; // Exit early, the new instance will handle things
            }
          }

          this.startDate = selectedDates[0]
            ? formatDateISO(selectedDates[0]) // Format the start date (timezone-safe)
            : '';
          this.endDate =
            mode === 'single'
              ? this.startDate
              : selectedDates[1]
              ? formatDateISO(selectedDates[1])
              : '';

          const dayElements =
            instance.daysContainer.querySelectorAll('.flatpickr-day');
          const startDate = selectedDates[0];

          // Enable nextMonth arrow
          if (instance && instance.nextMonthNav) {
            instance.nextMonthNav.classList.remove('flatpickr-disabled'); // Remove disabled class
            instance.nextMonthNav.setAttribute('aria-disabled', 'false'); // Reset accessibility attribute
            instance.nextMonthNav.onclick = null; // Re-enable navigation
          }

          // Re-enable the year selector
          if (instance && instance.currentYearElement) {
            instance.currentYearElement.removeAttribute('disabled'); // Re-enable the year selector
            instance.currentYearElement.classList.remove('flatpickr-disabled'); // Remove the disabled class
          }

          // Re-enable the month dropdown (if it exists)
          if (instance && instance.monthElements) {
            instance.monthElements.forEach((monthElement) => {
              monthElement.removeAttribute('disabled'); // Re-enable the month selector
              monthElement.classList.remove('flatpickr-disabled'); // Remove the disabled class
            });
          }

          if (selectedDates.length === 1) {
            // Disable all dates before the selected start date and restrict backward selection
            instance.set('disable', [
              function (date) {
                return date < startDate;
              },
            ]);

            // Update the minDate to prevent backward selection for end date
            instance.set('minDate', startDate);

            // Fetch unavailable dates again with the selected start date
            this.fetchUnavailableDates(
              startDate.getFullYear().toString(),
              (startDate.getMonth() + 1).toString(),
              instance,
              startDate,
            );

            // Handle Closed to Arrival Dates
            // These dates should be selectable as END dates, so don't fully disable them
            // Note: Tooltips are handled by updateFlatpickrDates function
            dayElements.forEach((dayElem) => {
              const ariaLabel = dayElem.getAttribute('aria-label');
              const dateStr = this.convertAriaLabelToDateStr(ariaLabel);
              const isClosedToArrival =
                this.closedToDays[dateStr]?.ct_start || false;

              if (isClosedToArrival && new Date(ariaLabel) >= startDate) {
                // Don't add flatpickr-disabled - these dates can be selected as end dates
                // Just add styling classes for visual feedback
                dayElem.classList.add('has-ct-arrival');
                dayElem.setAttribute('data-ct-arrival', 'true');
              }
            });
          }

          // Handle Closed to Departure Dates
          if (selectedDates.length === 2) {
            const endDate = selectedDates[1];

            dayElements.forEach((dayElem) => {
              const ariaLabel = dayElem.getAttribute('aria-label');
              const dateStr = this.convertAriaLabelToDateStr(ariaLabel);
              const isClosedToDeparture =
                this.minMaxDays[dateStr]?.ct_departure || false;

              if (isClosedToDeparture && new Date(ariaLabel) <= endDate) {
                if (new Date(ariaLabel).getTime() === endDate.getTime()) {
                  dayElem.classList.add('flatpickr-disabled');
                  dayElem.classList.add('disabled-ct-departure');
                  dayElem.removeAttribute('tabindex');
                }
              }
            });
          }

          // Apply min/max day restrictions within the selected range
          if (
            this.getEffectiveDateSelectionMode() === 'range' &&
            selectedDates.length === 2
          ) {
            const startDate = selectedDates[0];
            const endDate = selectedDates[1];
            let minDaysInRange = -Infinity;
            let maxDaysInRange = Infinity;

            dayElements.forEach((dayElem) => {
              const ariaLabel = dayElem.getAttribute('aria-label');
              const dateObj = new Date(ariaLabel);

              if (dateObj >= startDate && dateObj <= endDate) {
                const dateStr = this.convertAriaLabelToDateStr(ariaLabel);
                const minDays = this.minMaxDays[dateStr]?.min_days || -Infinity;
                const maxDays = this.minMaxDays[dateStr]?.max_days || Infinity;

                minDaysInRange = Math.max(minDaysInRange, minDays);
                maxDaysInRange = Math.min(maxDaysInRange, maxDays);
              }
            });
            const minDaysInRangeUser = minDaysInRange;
            const maxDaysInRangeUser = maxDaysInRange;
            if (this.product === 'Accommodation') {
              minDaysInRange += 1;
              maxDaysInRange += 1;
            }

            // Calculate day difference using timezone-safe utility
            const dayDifference = calculateDaysInclusive(startDate, endDate);

            let warningMessage = '';
            if (dayDifference < minDaysInRange) {
              const unit = this.product === 'Accommodation' ? 'nights' : 'days';
              warningMessage = `You must select at least ${minDaysInRangeUser} ${unit}.`;
            } else if (dayDifference > maxDaysInRange) {
              const unit = this.product === 'Accommodation' ? 'nights' : 'days';
              warningMessage = `You cannot select more than ${maxDaysInRangeUser} ${unit}.`;
            }

            if (warningMessage) {
              const warningElem = document.createElement('div');
              warningElem.id = 'flatpickr-warning';
              warningElem.className = 'flatpickr-warning';
              warningElem.innerHTML = `<i class="fas fa-exclamation-circle warning-icon"></i>${warningMessage}`;

              instance.calendarContainer.appendChild(warningElem);

              this.resetFlatpickrDates();
              instance.open(); // Re-open the Flatpickr instance

              setTimeout(() => {
                if (warningElem) {
                  warningElem.remove();
                }
              }, 3000);

              return;
            }
          }

          this.updateFlatpickrDates(product, startDate);

          // Only update price when we have both dates in range mode, or one date in single mode
          const isRangeMode = mode === 'range';
          const shouldUpdatePrice = isRangeMode
            ? this.startDate && this.endDate
            : this.startDate;

          if (shouldUpdatePrice) {
            this.updatePrice();
          }

          this.markFormChanged();

          if (this.startDate && this.endDate) {
            this.clearValidationError('startEndDate');
          }
        },
        onOpen: (selectedDates, dateStr, instance) => {
          console.log('onOpen CALLED!!!');
          // Reset preset date flag when picker opens
          this.presetDateJustSelected = false;
          // Clear selected dates and reset all restrictions on reopen
          // BUT don't clear if we're switching modes with a preserved start date
          if (!this.pendingStartDateForModeSwitch) {
            instance.clear();
          }
          instance.set('minDate', new Date(this.minDate));
          // If a selectedMonth exists in the store, use it to jump to the correct month
          if (this.selectedMonth) {
            const [year, month] = this.selectedMonth.split('-');
            instance.jumpToDate(new Date(year, month - 1)); // Flatpickr uses zero-indexed months
          }
          // Disable all dates when initially opened
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // +1 because getMonth() is zero-indexed
          // Fetch unavailable dates for the currently viewed month and year when the picker opens
          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
          );
        },
        onMonthChange: (selectedDates, dateStr, instance) => {
          console.log('onMonthChange CALLED!!!');
          // Disable all dates when month changes
          console.log('Disabling all dates when month changes');
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);

          const startDate = selectedDates[0];
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // +1 because getMonth() is zero-indexed
          this.selectedMonth = `${currentYear}-${String(currentMonth).padStart(
            2,
            '0',
          )}`; // Store in YYYY-MM format

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            startDate,
          );
        },
        onYearChange: (selectedDates, dateStr, instance) => {
          // Disable all dates when month changes
          instance.set('disable', [
            function () {
              return true; // Disable all dates initially
            },
          ]);
          const startDate = selectedDates[0];
          const currentYear = instance.currentYear;
          const currentMonth = instance.currentMonth + 1; // Adjusted to be 1-indexed
          this.selectedMonth = `${currentYear}-${String(currentMonth).padStart(
            2,
            '0',
          )}`;

          this.fetchUnavailableDates(
            currentYear.toString(),
            currentMonth.toString(),
            instance,
            startDate,
          );
        },
      });
    },
    fetchUnavailableDates(year, month, instance, startDate = null) {
      const csrfToken = getCSRFToken();

      // Add loading class to show pulsating animation
      if (instance && instance.calendarContainer) {
        instance.calendarContainer.classList.add('loading-availability');

        // 📅 Add preset-duration-mode class to hide prev/next month days
        if (this.hasPresetDurations) {
          instance.calendarContainer.classList.add('preset-duration-mode');
        } else {
          instance.calendarContainer.classList.remove('preset-duration-mode');
        }
      }

      // Check if we're using 2 months (range mode)
      const isTwoMonths = this.getEffectiveDateSelectionMode() === 'range';

      let requestData = {
        year: year,
        month: month,
        soldAs: this.soldAs,
        unitId: this.unitId,
        amountUnits: this.units,
        selectedPeople: this.selectedPeople,
        two_month_mode: isTwoMonths,
        context: 'store', // Identify this as Online Store (apply min constraints)
      };

      // Add start_date_selected to requestData if startDate is provided
      if (startDate) {
        requestData.start_date_selected = formatDateISO(startDate);
      }

      // 📅 Add preset duration if in preset mode
      if (this.hasPresetDurations && this.selectedPresetDuration) {
        requestData.presetDuration = this.selectedPresetDuration;
        console.log(
          '📅 Adding preset duration to request:',
          this.selectedPresetDuration,
        );
      }

      console.log('requestData:', requestData);

      postJSONWithCSRFRetry('/bookings_app/get-unavailable-dates/', requestData)
        .then((response) => response.json())
        .then((data) => {
          // 📅 If a preset date was just selected, skip all processing
          // This prevents the async fetch from resetting the flatpickr display
          if (this.presetDateJustSelected) {
            console.log(
              '📅 Skipping fetch response processing - preset date already selected',
            );
            this.presetDateJustSelected = false; // Reset the flag
            return;
          }

          this.categorizedDates = data.categorized_dates;

          // Use full unavailable_dates list from backend as source of truth.
          // This avoids frontend-only recategorization (e.g. synthetic `single2`) incorrectly
          // disabling valid start dates such as closed-to-arrival transitions.
          // Fallback to categorized_dates.middle for backward compatibility.
          this.unavailableDates =
            data.unavailable_dates || data.categorized_dates.middle;

          this.minMaxDays = data.min_max_days;
          this.closedToDays = data.closed_to_days;
          this.packages = data.packages || []; // Store package info

          // Check if we're in single mode and this month has min_days > 1 constraints
          // If so, auto-switch to multi-day mode (pre-detection on month navigation)
          if (
            this.showTourDurationToggle &&
            this.tourDuration === 'single' &&
            !this.pendingStartDateForModeSwitch // Don't interrupt an ongoing mode switch
          ) {
            const hasMinConstraint = Object.values(this.minMaxDays).some(
              (constraint) => constraint.min_days > 1,
            );

            if (hasMinConstraint) {
              console.log(
                '🔍 Month has min_days > 1 constraints, switching to multi-day mode',
              );

              // Store the current month so we reopen on it
              const currentMonth =
                this.selectedMonth ||
                `${year}-${String(month).padStart(2, '0')}`;

              // Switch to multi-day mode
              this.tourDuration = 'multi';

              // Reinitialize flatpickr in range mode
              if (instance) {
                const element = instance.input;
                instance.destroy();
                this.initializeFlatpickr(this.product, element, this.minDate);

                // Reopen the picker on the same month
                setTimeout(() => {
                  if (this.flatpickrInstances[this.product]) {
                    const newInstance = this.flatpickrInstances[this.product];
                    newInstance.open();

                    // Jump to the month the user was viewing
                    if (currentMonth) {
                      const [y, m] = currentMonth.split('-');
                      newInstance.jumpToDate(new Date(y, m - 1));
                    }
                  }
                }, 100);
              }

              return; // Exit early since we're reinitializing
            }
          }

          // 📦 If package has min/max people limits, enforce them
          if (data.package_max_people) {
            this.packageMaxPeople = data.package_max_people;
            console.log(
              '📦 Package max people limit set to:',
              this.packageMaxPeople,
            );
          }
          if (data.package_min_people) {
            this.packageMinPeople = data.package_min_people;
            console.log(
              '📦 Package min people requirement set to:',
              this.packageMinPeople,
            );
          }

          // Update person type options to apply the new limits
          if (data.package_max_people || data.package_min_people) {
            if (this.usePersonTypes) {
              this.updatePersonTypeOptions();
            }
          } else {
            this.packageMaxPeople = null;
          }

          console.log('data:', data);

          this.updateFlatpickrDates(this.product, startDate);

          // Package end-date constraints are validated in backend get-unavailable-dates.
          // Keep frontend as a renderer of backend unavailable_dates/closed_to_days.
          if (startDate && this.getEffectiveDateSelectionMode() === 'range') {
            // No packages, but still apply min and max constraint styling
            this.applyMinConstraintStyling(startDate, instance);
            this.applyMaxConstraintStyling(startDate, instance);
          }

          // Handle pending start date from mode switch (single to multi-day)
          // This must happen AFTER dates are enabled by updateFlatpickrDates
          if (this.pendingStartDateForModeSwitch && instance) {
            console.log(
              '📅 Setting pending start date after dates loaded:',
              this.pendingStartDateForModeSwitch,
            );

            // Small delay to ensure DOM is updated
            setTimeout(() => {
              if (this.pendingStartDateForModeSwitch) {
                // Set the start date - this puts flatpickr in "waiting for end date" state
                instance.setDate([this.pendingStartDateForModeSwitch], true);

                // Clear the pending date
                this.pendingStartDateForModeSwitch = null;

                console.log(
                  '✅ Start date pre-selected, waiting for end date selection',
                );
              }
            }, 50);
          }

          // Remove loading class after data is loaded
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.remove('loading-availability');
          }
        })
        .catch((error) => {
          console.error('Error fetching unavailable dates:', error);

          // Remove loading class even on error
          if (instance && instance.calendarContainer) {
            instance.calendarContainer.classList.remove('loading-availability');
          }
        });
    },
    updateFlatpickrDates(product, startDate = null) {
      const instance = this.flatpickrInstances[product];

      if (instance && instance.daysContainer) {
        // Disable unavailable dates always.
        // Before a start date is selected, also disable closed-to-arrival dates as potential starts.
        let disabledDates = [...this.unavailableDates];
        if (!startDate) {
          const closedToArrivalDates = Object.entries(this.closedToDays || {})
            .filter(([, flags]) => flags?.ct_start)
            .map(([dateStr]) => dateStr);
          disabledDates = [...disabledDates, ...closedToArrivalDates];
        }

        instance.set('disable', disabledDates);

        // Convert categorized_dates arrays into Set for efficient lookup
        this.categorizedDates = {
          start: new Set(this.categorizedDates.start),
          single: new Set(this.categorizedDates.single),
          single2: new Set(this.categorizedDates.single2),
          middle: new Set(this.categorizedDates.middle),
          end: new Set(this.categorizedDates.end),
        };
        const unavailableDateSet = new Set(this.unavailableDates);

        const currentMonth = instance.currentMonth; // Track the currently viewed month
        const currentYear = instance.currentYear; // Track the currently viewed year

        const dayElements =
          instance.daysContainer.querySelectorAll('.flatpickr-day'); // Get all day elements

        console.log('Unavailable dates:', this.unavailableDates);
        console.log('Categorized dates:', this.categorizedDates);

        // Loop through each day element and apply the appropriate classes and rules
        dayElements.forEach((dayElem) => {
          const ariaLabel = dayElem.getAttribute('aria-label');
          const dateStr = this.convertAriaLabelToDateStr(ariaLabel); // Convert to DD-MM-YYYY format
          const dateObj = new Date(ariaLabel); // Create Date object for comparison

          // Clear transient restriction markers before re-applying current state.
          dayElem.classList.remove(
            'has-ct-arrival',
            'flatpickr-disabled-start',
            'has-ct-departure',
            'flatpickr-disabled-end',
          );
          dayElem.removeAttribute('data-ct-arrival');
          dayElem.removeAttribute('data-ct-departure');
          dayElem.removeAttribute('data-max-exceeded');

          // Extract the month and year of the current date
          const elemMonth = dateObj.getMonth(); // Month is 0-indexed
          const elemYear = dateObj.getFullYear();

          // 📅 PRESET DURATION MODE: Skip processing for prevMonthDay and nextMonthDay cells
          // These are hidden via CSS (see preset-duration-mode class on calendarContainer)
          if (this.hasPresetDurations) {
            if (
              dayElem.classList.contains('prevMonthDay') ||
              dayElem.classList.contains('nextMonthDay')
            ) {
              return; // Skip further processing - these cells are hidden via CSS
            }
          }

          // Reset tooltips for each dayElem
          if (dayElem._tippyMinMax) {
            dayElem._tippyMinMax.destroy();
          }
          if (dayElem._tippyClosed) {
            dayElem._tippyClosed.destroy();
          }

          // Disable dates before the startDate, if startDate is selected, but only in the same month
          if (
            startDate &&
            dateObj < startDate &&
            elemMonth === currentMonth &&
            elemYear === currentYear
          ) {
            dayElem.classList.add('flatpickr-disabled');
            dayElem.removeAttribute('tabindex'); // Prevent focusing on the disabled date
            return; // Skip further checks for this date
          }

          // Apply custom styling based on categorized dates
          // 📅 Skip half-day styling in preset duration mode for accommodations
          // (user selects start date only, so show full dates as available/unavailable)
          const skipHalfDayStyling =
            this.hasPresetDurations && this.product === 'Accommodation';

          if (!skipHalfDayStyling) {
            if (this.categorizedDates.start.has(dateStr)) {
              dayElem.classList.add('start-date');
            }
            if (this.categorizedDates.single.has(dateStr)) {
              dayElem.classList.add('single-date');
            }
            if (this.categorizedDates.single2.has(dateStr)) {
              dayElem.classList.add('single2-date');
            }
            if (this.categorizedDates.middle.has(dateStr)) {
              dayElem.classList.add('middle-date');
            }
            if (this.categorizedDates.end.has(dateStr)) {
              dayElem.classList.add('end-date');
            }
          }

          // Check for closed-to-arrival or closed-to-departure restrictions
          const isClosedToArrival = this.closedToDays[dateStr]?.ct_start;
          const isClosedToDeparture = this.closedToDays[dateStr]?.ct_end;

          // Consolidated tooltip logic
          const applyTooltip = () => {
            if (!this.startDate) {
              // Before start date selection, only show closed to arrival
              if (isClosedToArrival) {
                dayElem._tippyClosed = tippy(dayElem, {
                  content: 'Closed to Arrival',
                  theme: 'light-border',
                  trigger: 'mouseenter focus',
                });
              }
              // No tooltip for available dates before start date selection
            } else {
              // After start date selection, only show closed-to-departure
              // Min constraint tooltips are handled by applyMinConstraintStyling
              if (isClosedToDeparture) {
                dayElem._tippyClosed = tippy(dayElem, {
                  content: 'Closed to Departure',
                  theme: 'light-border',
                  trigger: 'mouseenter focus',
                });
              }
              // No tooltip for available dates after start date selection
              // Note: Closed to arrival tooltips are hidden after start date selection
            }
          };

          // Initial State: When the picker opens
          if (!this.startDate) {
            // Apply closed-to-arrival restrictions (subtle styling, not fully disabled)
            if (isClosedToArrival) {
              dayElem.setAttribute('data-ct-arrival', isClosedToArrival);
              dayElem.classList.add('has-ct-arrival');
              dayElem.classList.add('flatpickr-disabled-start'); // Use lighter styling, not full gray background
              dayElem.removeAttribute('tabindex');
            }

            if (
              isClosedToArrival &&
              (this.categorizedDates.single.has(dateStr) ||
                this.categorizedDates.start.has(dateStr))
            ) {
              dayElem.setAttribute('data-ct-arrival', isClosedToArrival);
              dayElem.classList.add('has-ct-arrival');
              dayElem.classList.add('flatpickr-disabled-start');
              dayElem.removeAttribute('tabindex');
            }
            // Apply tooltip for initial state
            applyTooltip();
          } else if (this.startDate) {
            // After Start Date is Selected: Handle closed-to-arrival and closed-to-departure

            // Ensure the categorized dates (start, single, middle, end) still have their classes
            // 📅 Skip half-day styling in preset duration mode for accommodations
            if (!skipHalfDayStyling) {
              if (this.categorizedDates.start.has(dateStr)) {
                dayElem.classList.add('start-date');
              }
              if (this.categorizedDates.single.has(dateStr)) {
                dayElem.classList.add('single-date');
              }
              if (this.categorizedDates.single2.has(dateStr)) {
                dayElem.classList.add('single2-date');
              }
              if (this.categorizedDates.middle.has(dateStr)) {
                dayElem.classList.add('middle-date');
              }
              if (this.categorizedDates.end.has(dateStr)) {
                dayElem.classList.add('end-date');
              }
            }

            // Disable previous month arrow once startDate has been selected
            if (instance && instance.prevMonthNav) {
              instance.prevMonthNav.classList.add('flatpickr-disabled'); // Add a disabled class
              instance.prevMonthNav.setAttribute('aria-disabled', 'true'); // Set accessibility attribute
              instance.prevMonthNav.onclick = (e) => e.preventDefault(); // Disable navigation
            }

            // Enable start/single date styling hints only when the backend still marks
            // the date selectable. Never override explicit backend unavailability.
            if (
              (this.categorizedDates.start.has(dateStr) ||
                this.categorizedDates.single.has(dateStr)) &&
              !unavailableDateSet.has(dateStr)
            ) {
              dayElem.classList.remove('flatpickr-disabled');
              dayElem.setAttribute('tabindex', '0');
            }

            // Disable dates before the startDate, but only within the same month and year
            if (
              startDate &&
              dateObj < startDate &&
              elemMonth === currentMonth &&
              elemYear === currentYear
            ) {
              dayElem.classList.add('flatpickr-disabled');
              dayElem.removeAttribute('tabindex'); // Prevent focusing on the disabled date
              return; // Skip further checks for this date
            }

            // Do not infer an upper bound from categorized date classes in end-date mode.
            // Backend `unavailable_dates` is the source of truth for valid end dates.

            // Disable closed-to-departure dates (from API)
            if (isClosedToDeparture) {
              dayElem.setAttribute('data-ct-departure', isClosedToDeparture);
              dayElem.classList.add('has-ct-departure');
              dayElem.classList.add('flatpickr-disabled');
              dayElem.removeAttribute('tabindex');
            }

            // Apply tooltip for after startDate is selected
            // Min constraint styling is now applied by applyMinConstraintStyling() which runs later
            applyTooltip();
          }
        });

        // Show "Sorry, no availability this month" when current month has no selectable dates
        // Only show when no start date is selected (initial state)
        const existingNoDates = instance.calendarContainer.querySelector(
          '.flatpickr-no-dates-available',
        );
        if (existingNoDates) {
          existingNoDates.remove();
        }

        if (!startDate) {
          let availableInCurrentMonth = 0;
          dayElements.forEach((dayElem) => {
            // Skip prev/next month days
            if (
              dayElem.classList.contains('prevMonthDay') ||
              dayElem.classList.contains('nextMonthDay')
            ) {
              return;
            }
            // Count days that are not disabled
            if (!dayElem.classList.contains('flatpickr-disabled')) {
              availableInCurrentMonth += 1;
            }
          });

          if (availableInCurrentMonth === 0 && instance.calendarContainer) {
            const noDatesElem = document.createElement('div');
            noDatesElem.className = 'flatpickr-no-dates-available';
            noDatesElem.setAttribute('role', 'status');
            noDatesElem.textContent = 'Sorry, no availability this month :(';
            instance.calendarContainer.appendChild(noDatesElem);
          }
        }
      } else {
        console.error(`Flatpickr instance for '${product}' not found.`);
      }
    },
    populateFixedDateRangeDropdown(soldAsId) {
      console.log('FUNCTION CALLED: populateFixedDateRangeDropdown', {
        soldAsId: soldAsId,
        units: this.units,
        selectedPeople: this.selectedPeople,
      });

      // Fetch the fixed date ranges for the given soldAs ID
      const csrfToken = getCSRFToken();
      fetch('/bookings_app/fixed-date-ranges/', {
        method: 'POST',
        body: JSON.stringify({
          soldAs: soldAsId,
          amountUnits: this.units,
          selectedPeople: this.selectedPeople,
          source: 'store_booking_form',
        }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          console.log('Fixed date ranges response:', data);

          // Combine available and unavailable date ranges
          const allRanges = [
            ...data.available_fixed_date_ranges.map((range) => ({
              ...range,
              available: true,
            })),
            ...data.unavailable_fixed_date_ranges.map((range) => ({
              ...range,
              available: false,
            })),
          ];

          console.log('Combined ranges:', allRanges);

          // Sort the combined array by start date and then by end date
          allRanges.sort((a, b) => {
            const parseRangeDate = (value) => {
              const [day, month, year] = value.split('/');
              const parsed = parseDate(`${year}-${month}-${day}`);
              return parsed ? parsed.getTime() : 0;
            };
            const startDateComparison =
              parseRangeDate(a.date) - parseRangeDate(b.date);
            if (startDateComparison !== 0) return startDateComparison;
            return parseRangeDate(a.end_date) - parseRangeDate(b.end_date);
          });

          // Helper function to format dates from DD/MM/YYYY to a readable format
          const formatDate = (dateStr) => {
            const [day, month, year] = dateStr.split('/'); // Split date in DD/MM/YYYY format
            const date = parseDate(`${year}-${month}-${day}`); // Reconstruct date in YYYY-MM-DD format
            if (!date) return dateStr;
            return date.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            });
          };

          // Check if there are any ranges available
          if (allRanges.length === 0) {
            // No dates available - show message
            this.customDropdownOptions = [
              {
                display: 'No dates available at this time',
                value: '',
                startDate: '',
                endDate: '',
                available: false,
                disabled: true,
              },
            ];
          } else {
            // Format options for custom dropdown
            this.customDropdownOptions = allRanges.map((range) => {
              const isSingleDay = range.date === range.end_date;
              let displayText = '';

              if (isSingleDay) {
                displayText = `${formatDate(range.date)}${
                  range.available ? '' : ' (unavailable)'
                }`;
              } else {
                displayText = `${formatDate(range.date)} to ${formatDate(
                  range.end_date,
                )}${range.available ? '' : ' (unavailable)'}`;
              }

              return {
                display: displayText,
                value: `${range.date}-${range.end_date}`,
                startDate: range.date,
                endDate: range.end_date,
                available: range.available,
              };
            });
          }

          if (this.autoSelectFixedDateRange) {
            // Rateset page flow: auto-select first available fixed range.
            const firstAvailableOption = this.customDropdownOptions.find(
              (option) => option.available,
            );
            if (firstAvailableOption) {
              this.customDropdownSelected = firstAvailableOption;
              this.fixedDateRange = firstAvailableOption.value;
              this.startDate = this.convertDateToISO(
                firstAvailableOption.startDate,
              );
              this.endDate = this.convertDateToISO(
                firstAvailableOption.endDate,
              );
            } else {
              this.customDropdownSelected = null;
              this.fixedDateRange = '';
              this.startDate = '';
              this.endDate = '';
            }
          } else {
            // Advanced page flow: require explicit user selection.
            this.customDropdownSelected = null;
            this.fixedDateRange = '';
            this.startDate = '';
            this.endDate = '';
          }

          console.log('Fixed Rates populated successfully');
          this.updatePrice();
        })
        .catch((error) =>
          console.error('Error fetching fixed date ranges:', error),
        );
    },

    toggleCustomDropdown() {
      this.customDropdownOpen = !this.customDropdownOpen;
    },

    selectCustomDropdownOption(option) {
      this.customDropdownSelected = option;
      this.fixedDateRange = option.value;
      this.startDate = this.convertDateToISO(option.startDate);
      this.endDate = this.convertDateToISO(option.endDate);
      this.customDropdownOpen = false;
      this.updatePrice();

      // Clear date warning if both dates are set
      if (this.startDate && this.endDate) {
        this.clearValidationError('startEndDate');
      }
    },
    fixedDateSelection() {
      const dropdown = document.querySelector('.fixedDateRangeDropdown');
      const selectedOption = dropdown.options[dropdown.selectedIndex];
      if (selectedOption) {
        // Extract the dates from the option's attributes
        const startDateStr = selectedOption.getAttribute('data-start-date');
        const endDateStr = selectedOption.getAttribute('data-end-date');

        // Convert dates from DD/MM/YYYY to YYYY-MM-DD format
        const startDate = this.convertDateToISO(startDateStr);
        const endDate = this.convertDateToISO(endDateStr);

        // Update the store's date properties
        this.startDate = startDate;
        this.endDate = endDate;
        console.log(
          'Updated startDate and endDate:',
          this.startDate,
          this.endDate,
        );
        this.updatePrice();
        // Clear date warning if both dates are set
        if (this.startDate && this.endDate) {
          this.clearValidationError('startEndDate');
        }
      }
    },
    updatePrice() {
      const csrfToken = getCSRFToken();
      console.log('updatePrice!');
      console.log('soldAs:', this.soldAs);
      console.log('startDate:', this.startDate);
      console.log('endDate:', this.endDate);
      console.log('Units:', this.units);
      console.log('selectedPeople:', this.selectedPeople);

      if (this.soldAs && this.units) {
        console.log('this.fixedDateRange:', this.fixedDateRange);
        console.log('this.soldAsFixed:', this.soldAsFixed);
        let requestData;
        if (this.soldAsFixed === true) {
          console.log('FIXED INTERVALS');
          // Prepare the data to be sent (this can actually be simplified as fixed and flexi price is now calculated similar)
          requestData = {
            product: this.product,
            startDate: this.startDate,
            endDate: this.endDate,
            soldAs: this.soldAs,
            units: this.units,
            selectedPeople: this.selectedPeople,
          };
        } else {
          console.log('FLEXI DATES');
          // Prepare the data to be sent
          requestData = {
            product: this.product,
            startDate: this.startDate,
            soldAs: this.soldAs,
            units: this.units,
            selectedPeople: this.selectedPeople,
          };

          // Include endDate only if it's available
          if (this.endDate) {
            requestData.endDate = this.endDate;
          }
        }

        fetch('/bookings_app/calculate-price/', {
          // ADD functionality to retreive available dates from product (isavailable) and from rateset (hasrate)
          method: 'POST',
          body: JSON.stringify(requestData),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
        })
          .then((response) => response.json())
          .then((data) => {
            console.log('Server response:', data); // Log the response data
            this.price = data.price; // Update the price

            // Update discount information if present
            if (data.applied_discounts && data.applied_discounts.length > 0) {
              this.discountAmount = parseFloat(data.discount_amount) || 0;
              this.appliedDiscounts = data.applied_discounts;
            } else {
              this.discountAmount = 0;
              this.appliedDiscounts = [];
            }

            this.triggerPriceUpdateEffect();
            console.log('Updated price:', this.price); // Log the updated price
          })
          .catch((error) => {
            console.error('Error fetching price:', error);
          });
      } else {
        console.log('Start date or soldAs is not set');
      }
    },
    disableFlatpickr(flatpickrInstance, disable) {
      // Disable or enable the input field
      flatpickrInstance.input.disabled = disable;

      // Disable or enable the prev/next navigation buttons, if they exist
      if (flatpickrInstance.prevMonthNav && flatpickrInstance.nextMonthNav) {
        flatpickrInstance.prevMonthNav.disabled = disable;
        flatpickrInstance.nextMonthNav.disabled = disable;
      }

      // Optionally, disable the year and month dropdowns if present
      const pluginElements =
        flatpickrInstance.calendarContainer.querySelectorAll(
          '.flatpickr-monthDropdown-months, .numInput',
        );
      pluginElements.forEach((element) => {
        element.disabled = disable;
      });
    },
    triggerPriceUpdateEffect() {
      console.log('triggerPriceUpdate triggered!!!');
      this.priceUpdated = true;

      // Trigger CSS animation for price value
      const priceInput = document.querySelector('#price');
      if (priceInput) {
        priceInput.classList.add('price-animating');
        setTimeout(() => {
          priceInput.classList.remove('price-animating');
        }, 1800); // Match animation duration
      }

      setTimeout(() => {
        this.priceUpdated = false;
      }, 1800); // Duration of the background color effect
    },
    handleUnitChange() {
      console.log('FUNCTION CALLED: handleUnitChange', {
        units: this.units,
        soldAsFixed: this.soldAsFixed,
        soldAs: this.soldAs,
      });

      // Reset dates and price before handling the change
      this.resetDatesAndPrice();
      this.markFormChanged();

      // Update amount of people  for new amount of units
      this.generateAmountPeopleOptions();
      // Update person type options
      this.updatePersonTypeOptions();

      // Validate people vs units after unit change
      this.validatePeopleUnits();

      if (this.soldAsFixed === true) {
        console.log('Updating fixed date range dropdown for unit change');
        // Populate dropdown with options
        this.populateFixedDateRangeDropdown(this.soldAs);
      } else {
        console.log('Resetting flatpickr dates for unit change');
        // Reset Flatpickr dates
        this.resetFlatpickrDates();
      }
    },
    handleAmountPeopleChange() {
      console.log('FUNCTION CALLED: handleAmountPeopleChange', {
        selectedPeople: this.selectedPeople,
        soldAsFixed: this.soldAsFixed,
      });

      // Clear success message when user starts new entry
      this.clearSubmitMessage();
      this.markFormChanged();

      // Validate people vs units after people change
      this.validatePeopleUnits();

      // People count doesn't affect date availability, so just update price
      this.updatePrice();
    },
    generateAmountPeopleOptions() {
      console.log('FUNCTION CALLED: generateAmountPeopleOptions');
      console.log('peoplePerUnit:', this.peoplePerUnit);
      console.log('units:', this.units);

      const maxPeople = this.peoplePerUnit * this.units;
      console.log('maxPeople:', maxPeople);

      this.amountPeopleOptions = Array.from(
        { length: maxPeople },
        (_, i) => i + 1,
      );
      if (this.peopleAsOption == true) {
        this.selectedPeople = 1;
      } else {
        this.selectedPeople = null;
      }
    },
    resetDatesAndPrice() {
      // Clear dates and price
      this.startDate = '';
      this.endDate = '';
      this.price = 0;

      // Clear discount information
      this.discountAmount = 0;
      this.appliedDiscounts = [];

      // Reset fixed date range selection to the first option if needed
      this.fixedDateRange = '';

      console.log('Dates and price reset');
    },
    resetFlatpickrDates() {
      // Reset Flatpickr dates here
      Object.values(this.flatpickrInstances).forEach((flatpickrInstance) => {
        flatpickrInstance.clear(); // Clears the selected dates

        if (this.selectedMonth) {
          // Navigate to the selected month and year if provided
          const [year, month] = this.selectedMonth.split('-');
          flatpickrInstance.jumpToDate(new Date(year, month - 1)); // Flatpickr uses zero-indexed months
        }
      });

      console.log('Flatpickr dates reset');
    },
    resetExtrasVisibility() {
      // On rateset pages, extras should remain visible across "continue adding" cycles.
      const isRatesetPage =
        document.querySelector('[data-page-type="store-rateset"]') !== null;
      if (isRatesetPage) {
        return;
      }

      // Find the extras section and reset its visibility
      const extrasSection = document.querySelector('.extras-section');
      if (extrasSection) {
        const extrasData = Alpine.$data(extrasSection);
        if (extrasData) {
          extrasData.shouldShowExtras = false;
          extrasData.extrasInitialized = false;
          extrasData.hasUsedDatePicker = false;
        }
      }
    },
    clearFormFields() {
      // Set units back to 1
      this.units = 1;
      // set people back to 1 or 0 depending on peopleAsOption
      if (this.peopleAsOption == true) {
        this.selectedPeople = 1;
      } else {
        this.selectedPeople = null;
      }

      // Clear person type fields (Adults and Children)
      this.selectedAdults = 0;
      this.selectedChildren = 0;

      // Reset dates and price
      this.resetDatesAndPrice();

      // Reset validation errors (but keep submit messages for success flow)
      this.clearAllValidationErrors();

      if (this.soldAsFixed == true) {
        // Reset Fixed Date Range
        this.populateFixedDateRangeDropdown(this.soldAs);
      } else {
        // Reset Flatpickr dates
        this.resetFlatpickrDates();
      }
    },
    // Helper function to apply min/max tooltips for enabled days
    applyMinMaxTooltip(dayElem, dateStr) {
      const { min_days, max_days } = this.minMaxDays[dateStr] || {};

      if (min_days || max_days) {
        const minContent = min_days ? `Min ${min_days}` : '';
        const maxContent = max_days ? `Max ${max_days}` : '';
        const tooltipContent =
          minContent && maxContent
            ? `${minContent}, ${maxContent}`
            : minContent || maxContent;

        if (tooltipContent) {
          dayElem._tippyMinMax = tippy(dayElem, {
            content: tooltipContent,
            theme: 'light-border',
            trigger: 'mouseenter focus',
          });
        }
      }
    },
    convertDateToISO(dateStr) {
      const parts = dateStr.split('/');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    },
    convertAriaLabelToDateStr(ariaLabel) {
      const date = new Date(ariaLabel);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is zero-indexed
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    setPlaceholder() {
      // 📅 Preset durations: user selects start date only
      if (this.hasPresetDurations) {
        return 'Select start date';
      }
      return this.getEffectiveDateSelectionMode() === 'single'
        ? 'Select a day'
        : 'Select dates';
    },
    validateForm(event, action) {
      // Guard against double-firing from touchstart + click on Safari iOS
      const now = Date.now();
      if (now - this.lastValidationTime < 300) {
        console.log(
          '⏭️ Skipping duplicate validation call (touchstart + click guard)',
        );
        return;
      }
      this.lastValidationTime = now;

      if (event && event.preventDefault) {
        event.preventDefault(); // Prevent form submission by default
      }

      let form = null;
      if (event && event.target) {
        form = event.target.closest('form');
        console.log('🔍 Trying event.target.closest("form"):', form);
      }

      if (!form) {
        form = document.querySelector('.bookingSideForm');
        console.log(
          '🔍 Trying document.querySelector(".bookingSideForm"):',
          form,
        );
      }

      if (!form) {
        form = document.querySelector('form');
        console.log('🔍 Trying document.querySelector("form"):', form);
      }

      console.log('🔍 validateForm - final form:', form);
      console.log('🔍 validateForm - action:', action);

      // Ensure the start and end dates are defined
      if (!this.startDate || !this.endDate) {
        this.setValidationError('startEndDate', 'Please select valid dates');
        return;
      }

      // Validate people vs units
      if (!this.validatePeopleUnits()) {
        this.setValidationError(
          'peopleUnits',
          this.validationErrors.peopleUnits,
        );
        return;
      }

      // Validate person types first
      if (this.usePersonTypes) {
        if (
          this.unitType === 'Person' ||
          this.unitType === 'Adult' ||
          this.unitType === 'Child'
        ) {
          // Require at least one adult or child for Person-type units
          const totalPeople =
            (parseInt(this.selectedAdults) || 0) +
            (parseInt(this.selectedChildren) || 0);
          if (totalPeople === 0) {
            this.setValidationError(
              'peopleUnits',
              'Please select at least one adult or child.',
            );
            return;
          }
        } else {
          // Existing logic for other unit types
          const totalPeople =
            (parseInt(this.selectedAdults) || 0) +
            (parseInt(this.selectedChildren) || 0);
          if (totalPeople === 0) {
            this.setValidationError(
              'peopleUnits',
              'Please select the number of people',
            );
            return;
          }
          if (totalPeople > this.maxPersons) {
            this.setValidationError(
              'peopleUnits',
              `Maximum ${this.maxPersons} people allowed`,
            );
            return;
          }
        }
      }

      // Check form validity last
      console.log('🔍 About to check form validity - form:', form);
      if (form && form.checkValidity()) {
        console.log('✅ Form is valid, calling submitForm');
        this.submitForm(event, action, form);
      } else if (form) {
        console.log('❌ Form is invalid, calling reportValidity');
        form.reportValidity();
      } else {
        console.log('❌ No form found, cannot validate');
      }
    },
    async submitForm(event, button, formElement = null) {
      console.log('=== SUBMIT FORM START ===');
      console.log('Button:', button);
      console.log('Form element passed:', formElement);

      if (event && event.preventDefault) {
        event.preventDefault();
      }

      // Sync session ID before submission
      this.syncSessionId();

      // Log all session identifiers
      console.log('Session State:', {
        currentSessionId: this.sessionId,
        sessionStorageId: sessionStorage.getItem('bookingSessionId'),
        localStorageId: localStorage.getItem('bookingSessionId'),
      });

      // Get cart store reference
      const storeCartStore = Alpine.store('storeCartStore');
      console.log('Cart Store State:', {
        editModeBooking: storeCartStore.editModeBooking,
        bookingItemsCount: storeCartStore.bookingItemsCount,
        storeSessionId: storeCartStore.sessionId,
      });

      // Get form element and create FormData
      let finalFormElement = formElement;
      if (!finalFormElement && event && event.target) {
        finalFormElement = event.target.closest('form') || event.target.form;
      }
      if (!finalFormElement) {
        finalFormElement = document.querySelector('.bookingSideForm');
      }

      if (!finalFormElement) {
        console.error('Form element not found');
        return;
      }

      console.log('Using form element:', finalFormElement);
      const formData = new FormData(finalFormElement);

      // Ensure session consistency
      const sessionId = sessionStorage.getItem('bookingSessionId');
      if (sessionId !== this.sessionId) {
        console.warn('Session ID mismatch:', {
          storeSessionId: this.sessionId,
          storageSessionId: sessionId,
        });
        // Update session ID if needed
        this.sessionId = sessionId;
      }
      console.log('Initial Form Data:', Object.fromEntries(formData));
      console.log('Person Type State:', {
        usePersonTypes: this.usePersonTypes,
        unitType: this.unitType,
        selectedAdults: this.selectedAdults,
        selectedChildren: this.selectedChildren,
      });

      // Handle person type data based on unit type and usePersonTypes
      if (this.usePersonTypes) {
        if (this.unitType === 'Person') {
          formData.set('person_type', this.personType);
          console.log('Setting person_type:', this.personType);
        } else {
          // Ensure adults and children are added if usePersonTypes is true
          const adults = this.selectedAdults || '0';
          const children = this.selectedChildren || '0';
          formData.set('amount_adults', adults);
          formData.set('amount_children', children);
          console.log(
            'Adding person types - adults:',
            adults,
            'children:',
            children,
          );
        }
      }

      console.log('Final Form Data:', Object.fromEntries(formData));

      // Add rate set extras to form data if any are selected
      const storeRateSetStore = Alpine.store('storeRateSetStore');
      if (storeRateSetStore && storeRateSetStore.hasSelectedExtras()) {
        const selectedExtras = storeRateSetStore.getSelectedExtras();
        console.log(
          'Adding rate set extras to form submission:',
          selectedExtras,
        );

        // Add each selected extra to the form data
        selectedExtras.forEach((selectedExtra, index) => {
          formData.append(`extras[${index}][extra_id]`, selectedExtra.extra_id);
          formData.append(`extras[${index}][quantity]`, selectedExtra.quantity);
          if (selectedExtra.dropdown_selection) {
            formData.append(
              `extras[${index}][dropdown_selection]`,
              selectedExtra.dropdown_selection,
            );
          }
        });
      }

      // Add custom field values to form data
      if (this.customFields && Object.keys(this.customFields).length > 0) {
        console.log('Adding custom fields:', this.customFields);
        Object.keys(this.customFields).forEach((fieldName) => {
          const value = this.customFields[fieldName];
          if (value !== null && value !== undefined && value !== '') {
            formData.append(`custom_field_${fieldName}`, value);
          }
        });
      }

      // Add per-person custom field values to form data
      if (this.customFieldsPerPerson && this.customFieldsPerPerson.length > 0) {
        console.log(
          'Adding per-person custom fields:',
          this.customFieldsPerPerson,
        );
        this.customFieldsPerPerson.forEach((personFields, personIndex) => {
          Object.keys(personFields).forEach((fieldName) => {
            const value = personFields[fieldName];
            if (value !== null && value !== undefined && value !== '') {
              formData.append(
                `custom_field_${fieldName}_person_${personIndex}`,
                value,
              );
            }
          });
        });
      }

      // Add required fields
      formData.append('session_id', this.sessionId);
      formData.append('action', 'continue_adding');

      // Ensure amount_units is always set (especially for Package RateSets without direct products)
      if (!formData.has('amount_units')) {
        formData.set('amount_units', this.units || 1);
      }

      // Log complete submission data
      console.log('Submission Details:', {
        formData: Object.fromEntries(formData),
        storeState: {
          sessionId: this.sessionId,
          startDate: this.startDate,
          endDate: this.endDate,
          units: this.units,
          selectedPeople: this.selectedPeople,
          usePersonTypes: this.usePersonTypes,
          selectedAdults: this.selectedAdults,
          selectedChildren: this.selectedChildren,
          price: this.price,
        },
      });

      // Update UI to show loading state
      this.isSubmitting = true;

      // Make API request
      fetchWithTimeout(
        '/bookings_app/create-temporary-booking/',
        {
          method: 'POST',
          body: formData,
          headers: {
            'X-CSRFToken': getCSRFToken(),
          },
          credentials: 'same-origin',
        },
        30000,
      )
        .then(async (response) => {
          console.log('API Response Status:', response.status);
          // Parse JSON response
          const data = await response.json();
          console.log('API Response Data:', data);

          // If response is not ok, throw error with the server's error message
          if (!response.ok) {
            throw new Error(
              data.error || `HTTP error! status: ${response.status}`,
            );
          }

          return data;
        })
        .then(async (data) => {
          // Success handling
          console.log('Booking created successfully:', data);

          // Update cart store
          storeCartStore.editModeBooking = data.booking;
          storeCartStore.bookingItemsCount = data.booking_items_count;
          storeCartStore.updateBookingCartUrl();

          // Initialize cart store session for extras management
          if (data.booking && data.booking.id) {
            storeCartStore.sessionId = this.sessionId;
            storeCartStore.calendarId = data.booking.calendar_id;
            storeCartStore.calendarSlug = data.booking.calendar_slug;
          }

          // Rate set extras are now handled via form submission

          // Show success message with View Cart link
          this.setSubmitMessage('Added to cart!', 'success');

          // Update with link if URL is available
          if (storeCartStore.bookingUrl) {
            setTimeout(() => {
              const viewCartLink =
                ' <a href="' +
                storeCartStore.bookingUrl +
                '" class="text-dark fw-bold text-decoration-underline ms-1" style="color: #155724 !important;">View Cart</a>';
              this.setSubmitMessage('Added to cart!' + viewCartLink, 'success');
            }, 100);
          }

          // Collapse the form after successful submission
          this.isExpanded = false;

          // Clear the form fields
          this.clearFormFields();

          // Check if we're on the rateset page (where extras should remain visible)
          const pageTypeElement = document.querySelector(
            '[data-page-type="store-rateset"]',
          );
          const isRatesetPage = pageTypeElement !== null;

          if (storeRateSetStore) {
            if (isRatesetPage) {
              // On rateset page: only reset selected values, keep extras visible
              storeRateSetStore.resetSelectedExtras();
            } else {
              // On side form: clear everything (extras will disappear)
              if (storeRateSetStore.clearAllExtrasState) {
                storeRateSetStore.clearAllExtrasState();
              }
            }
          }

          // Reset extras visibility (only affects side form)
          this.resetExtrasVisibility();

          // Clear the unsaved changes flag at the very end after all operations
          this.clearFormChanges();

          // Don't show separate View Cart button anymore
          this.showViewCartButton = false;

          // Optional: Trigger success event
          window.dispatchEvent(
            new CustomEvent('booking:created', {
              detail: data,
            }),
          );
        })
        .catch((error) => {
          console.error('Error creating booking:', {
            error: error,
            message: error.message,
            stack: error.stack,
          });

          // Show error message from server or fallback message
          this.setSubmitMessage(
            error.message || 'Failed to create booking. Please try again.',
            'error',
          );

          window.dispatchEvent(
            new CustomEvent('booking:error', {
              detail: error,
            }),
          );
        })
        .finally(() => {
          // Reset submitting state
          this.isSubmitting = false;
        });
    },
    // Add method to synchronize session ID
    syncSessionId() {
      const storedSessionId = sessionStorage.getItem('bookingSessionId');
      if (storedSessionId && storedSessionId !== this.sessionId) {
        console.log('Synchronizing session ID:', {
          old: this.sessionId,
          new: storedSessionId,
        });
        this.sessionId = storedSessionId;
      }
    },

    updatePersonTypeOptions() {
      console.log('FUNCTION CALLED: updatePersonTypeOptions');

      // If not using person types, reset and return
      if (!this.usePersonTypes) {
        this.adultOptions = [];
        this.childrenOptions = [];
        return;
      }

      // Calculate maxPersons based on unit type
      // Person, Adult, and Child are all person-type units
      if (
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child'
      ) {
        // For Person-type units, use totalUnits as maxPersons
        this.maxPersons = parseInt(this.totalUnits) || 1;

        // 📦 If package has a max people limit, enforce it
        console.log('📦 Before package limit check:', {
          packageMaxPeople: this.packageMaxPeople,
          maxPersons: this.maxPersons,
          totalUnits: this.totalUnits,
          unitType: this.unitType,
        });
        if (this.packageMaxPeople && this.maxPersons > this.packageMaxPeople) {
          console.log(
            `📦 Limiting maxPersons from ${this.maxPersons} to packageMaxPeople: ${this.packageMaxPeople}`,
          );
          this.maxPersons = this.packageMaxPeople;
        }

        console.log(
          `${this.unitType} type - maxPersons set to:`,
          this.maxPersons,
        );
      } else {
        // For other types, calculate based on units * peoplePerUnit
        if (this.peoplePerUnit == null || this.units == null) {
          console.warn(
            'peoplePerUnit or units is null, cannot generate options',
          );
          this.adultOptions = [0];
          this.childrenOptions = [0];
          return;
        }

        this.maxPersons = this.peoplePerUnit * this.units;
        console.log('Other type - maxPersons calculated:', {
          peoplePerUnit: this.peoplePerUnit,
          units: this.units,
          maxPersons: this.maxPersons,
        });
      }

      // 📦 If package has a max people limit, enforce it
      if (this.packageMaxPeople && this.maxPersons > this.packageMaxPeople) {
        console.log(
          `📦 Limiting maxPersons from ${this.maxPersons} to packageMaxPeople: ${this.packageMaxPeople}`,
        );
        this.maxPersons = this.packageMaxPeople;
      }

      // Update adults options based on selected children (no minimum restriction in dropdown)
      if (this.selectedChildren !== null) {
        const maxAdults = this.maxPersons - parseInt(this.selectedChildren);
        this.adultOptions = Array.from({ length: maxAdults + 1 }, (_, i) => i);
      } else {
        this.adultOptions = Array.from(
          { length: this.maxPersons + 1 },
          (_, i) => i,
        );
      }

      // Update children options based on selected adults (no minimum restriction in dropdown)
      if (this.selectedAdults !== null) {
        const maxChildren = this.maxPersons - parseInt(this.selectedAdults);
        this.childrenOptions = Array.from(
          { length: maxChildren + 1 },
          (_, i) => i,
        );
      } else {
        this.childrenOptions = Array.from(
          { length: this.maxPersons + 1 },
          (_, i) => i,
        );
      }

      // Set default values if not already set
      if (this.selectedAdults === null) this.selectedAdults = 0;
      if (this.selectedChildren === null) this.selectedChildren = 0;

      console.log('Updated options:', {
        adultOptions: this.adultOptions,
        childrenOptions: this.childrenOptions,
        selectedAdults: this.selectedAdults,
        selectedChildren: this.selectedChildren,
        maxPersons: this.maxPersons,
      });
    },

    handlePersonTypeChange() {
      console.log('FUNCTION CALLED: handlePersonTypeChange', {
        selectedAdults: this.selectedAdults,
        selectedChildren: this.selectedChildren,
        unitType: this.unitType,
        soldAsFixed: this.soldAsFixed,
      });

      // Clear success message when user starts new entry
      this.clearSubmitMessage();
      this.markFormChanged();

      // Update both fields' options
      this.updatePersonTypeOptions();

      // Validate people counts (including package minimum requirements)
      this.validatePeopleUnits();

      if (
        this.unitType === 'Person' ||
        this.unitType === 'Adult' ||
        this.unitType === 'Child'
      ) {
        // For Person-type units, update units with the total
        this.units =
          parseInt(this.selectedAdults || 0) +
          parseInt(this.selectedChildren || 0);
        console.log('Units updated:', {
          adults: this.selectedAdults,
          children: this.selectedChildren,
          totalUnits: this.units,
          unitType: this.unitType,
        });
        // Reset dates and price because this is changing units selection
        if (this.soldAsFixed === true) {
          // Populate dropdown with options
          this.populateFixedDateRangeDropdown(this.soldAs);
        } else {
          // Reset Flatpickr dates
          this.resetFlatpickrDates();
        }
      } else {
        // For other unit types, update selectedPeople with the total
        this.selectedPeople =
          parseInt(this.selectedAdults || 0) +
          parseInt(this.selectedChildren || 0);
        console.log('People updated:', {
          adults: this.selectedAdults,
          children: this.selectedChildren,
          totalPeople: this.selectedPeople,
        });

        // For packages, people count CAN affect availability (PER_PERSON components)
        // So we need to re-populate the dropdown for fixed date packages
        if (this.soldAsFixed === true) {
          this.populateFixedDateRangeDropdown(this.soldAs);
        }
      }

      // Update price after all changes
      this.updatePrice();

      // Validate people vs units after person type change
      this.validatePeopleUnits();
    },

    async initializeOptions() {
      console.log('FUNCTION CALLED: initializeOptions', {
        usePersonTypes: this.usePersonTypes,
        peopleAsOption: this.peopleAsOption,
        unitType: this.unitType,
      });

      // Reset view cart button state on initialization
      this.showViewCartButton = false;
      console.log(
        'View Cart button reset on initialization:',
        this.showViewCartButton,
      );

      // 📅 Check for preset durations FIRST - they override tour duration mode
      this.presetDurations = this.selectedRateSet?.preset_durations || [];
      this.hasPresetDurations = this.presetDurations.length > 0;

      if (this.hasPresetDurations) {
        // Preset durations mode - hide single/multi toggle, use single date selection
        this.showTourDurationToggle = false;
        this.tourDuration = 'single'; // Always single date selection in preset mode

        // Set default preset duration
        const defaultDuration =
          this.presetDurations.find((d) => d.is_default) ||
          this.presetDurations[0];
        this.selectedPresetDuration = defaultDuration.duration;

        console.log('📅 Preset durations mode active:', this.presetDurations);
        console.log('📅 Default preset duration:', this.selectedPresetDuration);
      } else if (
        this.product === 'Tour' &&
        this.dateSelectType === 'range' &&
        !this.soldAsFixed
      ) {
        // Normal tour duration toggle mode
        switch (this.tourDurationMode) {
          case 'single_only':
            this.showTourDurationToggle = false;
            this.tourDuration = 'single';
            break;
          case 'multi_only':
            this.showTourDurationToggle = false;
            this.tourDuration = 'multi';
            break;
          case 'both_multi_default':
            this.showTourDurationToggle = true;
            this.tourDuration = 'multi';
            break;
          case 'both_single_default':
          default:
            this.showTourDurationToggle = true;
            this.tourDuration = 'single';
            break;
        }
        console.log(
          '🎛️ Tour duration mode:',
          this.tourDurationMode,
          '→ toggle:',
          this.showTourDurationToggle,
          ', default:',
          this.tourDuration,
        );
      } else {
        this.showTourDurationToggle = false;
      }

      // 📦 Package max people is now set from template context on page load
      console.log('📦 packageMaxPeople value:', this.packageMaxPeople);
      if (this.packageMaxPeople) {
        console.log(
          '📦 Package max people limit initialized from template:',
          this.packageMaxPeople,
        );
      } else {
        console.log(
          '📦 No package max people limit set (packageMaxPeople is null/undefined)',
        );
      }

      // Set options immediately (packageMaxPeople is already available from template)
      if (this.usePersonTypes) {
        this.updatePersonTypeOptions();
      } else if (this.peopleAsOption) {
        this.generateAmountPeopleOptions();
      }

      // Watch for units changes to clear success message
      if (typeof this.$watch === 'function') {
        this.$watch('units', () => {
          this.clearSubmitMessage();
        });
      }

      // For tours with range capability, check if we should default to multi-day mode
      if (this.showTourDurationToggle) {
        await this.checkTourDurationDefault();
      }
    },

    async checkTourDurationDefault() {
      console.log(
        '🔍 Checking if tour should default to multi-day mode on init',
      );

      const csrfToken = getCSRFToken();
      const currentDate = new Date();

      try {
        const response = await fetch('/bookings_app/get-unavailable-dates/', {
          method: 'POST',
          body: JSON.stringify({
            year: currentDate.getFullYear(),
            month: currentDate.getMonth() + 1,
            soldAs: this.soldAs,
            unitId: null,
            amountUnits: 1,
            selectedPeople: 1,
            two_month_mode: false,
            context: 'store',
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
        });

        const data = await response.json();
        this.minMaxDays = data.min_max_days || {};

        console.log('  Received minMaxDays:', this.minMaxDays);

        // Check if any date has min_days > 1
        const hasMinConstraint = Object.values(this.minMaxDays).some(
          (constraint) => constraint.min_days > 1,
        );

        console.log('  hasMinConstraint:', hasMinConstraint);

        if (hasMinConstraint && this.tourDuration === 'single') {
          console.log(
            '🔄 Tour has min length > 1, defaulting to multi-day mode',
          );
          this.tourDuration = 'multi';

          // If flatpickr already exists, reinitialize it with range mode
          // (flatpickr might be initialized before this async method completes)
          if (this.flatpickrInstances[this.product]) {
            console.log(
              '🔄 Flatpickr already initialized, reinitializing in range mode',
            );
            const element = this.flatpickrInstances[this.product].input;
            this.flatpickrInstances[this.product].destroy();
            this.initializeFlatpickr(this.product, element, this.minDate);
          } else {
            console.log(
              '  Flatpickr not initialized yet, will use correct mode on init',
            );
          }
        } else {
          console.log('  Staying in single mode');
        }
      } catch (error) {
        console.error('Error checking tour duration default:', error);
      }
    },

    // Method to handle tour duration change
    handleTourDurationChange() {
      console.log('FUNCTION CALLED: handleTourDurationChange');

      // Clear success message when user starts new entry
      this.clearSubmitMessage();

      // Reset dates when switching modes
      this.startDate = '';
      this.endDate = '';
      this.price = 0;

      // Use existing method to clear flatpickr dates
      this.resetFlatpickrDates();

      // Reset extras state so they can be re-initialized after new date selection
      // Dispatch a custom event to notify the extras section
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('tour-duration-changed'));
      }, 50);

      // Determine the new mode
      const newMode = this.tourDuration === 'single' ? 'single' : 'range';

      // Reinitialize flatpickr with new mode if instance exists
      if (this.flatpickrInstances[this.product]) {
        const element = this.flatpickrInstances[this.product].input;

        // Clear the input field visually as well
        element.value = '';
        if (
          element.nextElementSibling &&
          element.nextElementSibling.classList.contains('input')
        ) {
          element.nextElementSibling.value = ''; // Clear alt input if it exists
        }

        // Destroy current instance
        this.flatpickrInstances[this.product].destroy();

        // Reinitialize with new mode
        this.initializeFlatpickr(this.product, element, this.minDate);
      }
    },

    // 📅 Method to handle preset duration change
    handlePresetDurationChange() {
      console.log(
        '📅 Preset duration changed to:',
        this.selectedPresetDuration,
      );

      // Clear success message when user starts new entry
      this.clearSubmitMessage();

      // Reset dates when switching durations (need to re-select start date)
      this.startDate = '';
      this.endDate = '';
      this.price = 0;

      // Use existing method to clear flatpickr dates
      this.resetFlatpickrDates();

      // Reset extras state so they can be re-initialized after new date selection
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('preset-duration-changed'));
      }, 50);

      // Reinitialize flatpickr to refresh availability for new duration
      if (this.flatpickrInstances[this.product]) {
        const element = this.flatpickrInstances[this.product].input;

        // Clear the input field visually as well
        element.value = '';
        if (
          element.nextElementSibling &&
          element.nextElementSibling.classList.contains('input')
        ) {
          element.nextElementSibling.value = '';
        }

        // Destroy current instance
        this.flatpickrInstances[this.product].destroy();

        // Reinitialize flatpickr (will fetch new availability with updated preset duration)
        this.initializeFlatpickr(this.product, element, this.minDate);
      }
    },

    // Method to switch from single to multi-day mode while preserving the selected start date
    switchToMultiDayWithDate(startDate) {
      console.log(
        '🔄 Switching to multi-day mode with preserved date:',
        startDate,
      );

      // Store the date to preserve through the mode switch
      this.pendingStartDateForModeSwitch = startDate;

      // Switch to multi-day mode
      this.tourDuration = 'multi';

      // Clear current dates (will be restored after reinitialization)
      this.startDate = '';
      this.endDate = '';
      this.price = 0;

      // Dispatch event for extras section
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('tour-duration-changed'));
      }, 50);

      // Reinitialize flatpickr in range mode
      if (this.flatpickrInstances[this.product]) {
        const element = this.flatpickrInstances[this.product].input;

        // Clear the input field visually
        element.value = '';
        if (
          element.nextElementSibling &&
          element.nextElementSibling.classList.contains('input')
        ) {
          element.nextElementSibling.value = '';
        }

        // Destroy current instance
        this.flatpickrInstances[this.product].destroy();

        // Reinitialize with range mode
        this.initializeFlatpickr(this.product, element, this.minDate);

        // Open the picker - the pending date will be set after fetchUnavailableDates completes
        setTimeout(() => {
          if (this.flatpickrInstances[this.product]) {
            this.flatpickrInstances[this.product].open();
          }
        }, 100);
      }
    },

    clearValidationError(field) {
      this.validationErrors = {
        ...this.validationErrors,
        [field]: '',
      };
    },

    formatDateDDMMYYYY(date) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },

    applyMinConstraintStyling(startDate, instance) {
      if (!startDate || !instance) return;

      console.log('⚙️ Applying min constraint "closed to departure" styling');

      // Get start date as Date object
      const startDateObj = parseDate(startDate);
      if (!startDateObj) return;
      const startDateStr = this.formatDateDDMMYYYY(startDateObj);
      const minRequired = this.minMaxDays[startDateStr]?.min_days || 1;

      // Calculate minEndDate
      const minEndDate = new Date(startDateObj);
      if (this.product === 'Accommodation') {
        minEndDate.setDate(minEndDate.getDate() + minRequired);
      } else {
        minEndDate.setDate(minEndDate.getDate() + minRequired - 1);
      }

      console.log(
        `  Start: ${startDateStr}, Min required: ${minRequired}, Min end: ${this.formatDateDDMMYYYY(
          minEndDate,
        )}`,
      );
      console.log(
        `  Start date obj: ${startDateObj.toDateString()}, Min end date obj: ${minEndDate.toDateString()}`,
      );

      // Get all day elements
      const dayElements = instance.calendarContainer.querySelectorAll(
        '.flatpickr-day:not(.prevMonthDay):not(.nextMonthDay)',
      );

      let markedCount = 0;
      let skippedCount = 0;

      dayElements.forEach((dayElem) => {
        const ariaLabel = dayElem.getAttribute('aria-label');
        if (!ariaLabel) return;

        const dateObj = new Date(ariaLabel);
        const dateStr = this.convertAriaLabelToDateStr(ariaLabel);

        // Check if this date violates min constraint
        const violatesMinConstraint =
          dateObj > startDateObj && dateObj < minEndDate;

        // Special debug for Oct 26
        if (dateStr === '26-10-2025') {
          console.log(`🐛 DEBUG Oct 26:`);
          console.log(`  dateObj: ${dateObj.toISOString()}`);
          console.log(`  startDateObj: ${startDateObj.toISOString()}`);
          console.log(`  minEndDate: ${minEndDate.toISOString()}`);
          console.log(`  dateObj > startDateObj: ${dateObj > startDateObj}`);
          console.log(`  dateObj < minEndDate: ${dateObj < minEndDate}`);
          console.log(`  violatesMinConstraint: ${violatesMinConstraint}`);
          console.log(`  Classes: ${dayElem.className}`);
        }

        if (violatesMinConstraint) {
          // Check if it's already marked as closed to departure from API
          const isClosedToDeparture = this.closedToDays[dateStr]?.ct_end;
          const isClosedToArrival = this.closedToDays[dateStr]?.ct_start;

          console.log(
            `  🔍 ${dateStr}: violates min (dateObj=${dateObj.toDateString()}, ct_end=${isClosedToDeparture}, ct_start=${isClosedToArrival})`,
          );

          if (!isClosedToDeparture) {
            // Apply "closed to departure" styling
            dayElem.setAttribute('data-ct-departure', 'true');
            dayElem.classList.add('has-ct-departure');
            dayElem.classList.add('flatpickr-disabled-end');
            dayElem.removeAttribute('tabindex');

            // Use capture phase to prevent clicks
            dayElem.addEventListener(
              'click',
              (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
              },
              true,
            );

            // Apply tooltip with min constraint info
            if (dayElem._tippyClosed) {
              dayElem._tippyClosed.destroy();
            }
            const unitLabel =
              this.product === 'Accommodation' ? 'nights' : 'days';
            dayElem._tippyClosed = tippy(dayElem, {
              content: `Min ${minRequired} ${unitLabel}`,
              theme: 'light-border',
              trigger: 'mouseenter focus',
            });

            markedCount++;
            console.log(`  ✅ Applied to ${dateStr} (count=${markedCount})`);
          } else {
            skippedCount++;
            console.log(
              `  ⏭️  Skipped ${dateStr} (already ct_end, count=${skippedCount})`,
            );
          }
        }
      });

      console.log(
        `  📊 Summary: Marked ${markedCount} dates, Skipped ${skippedCount} dates`,
      );
    },

    applyMaxConstraintStyling(startDate, instance) {
      if (!startDate || !instance) return;

      console.log('⚙️ Applying max constraint "disabled after max" styling');

      // Get start date as Date object
      const startDateObj = parseDate(startDate);
      if (!startDateObj) return;
      const startDateStr = this.formatDateDDMMYYYY(startDateObj);
      const maxAllowed = this.minMaxDays[startDateStr]?.max_days;

      // If no max constraint, skip
      if (!maxAllowed) {
        console.log('  No max constraint found, skipping');
        return;
      }

      // Calculate maxEndDate (last valid date)
      const maxEndDate = new Date(startDateObj);
      if (this.product === 'Accommodation') {
        maxEndDate.setDate(maxEndDate.getDate() + maxAllowed);
      } else {
        maxEndDate.setDate(maxEndDate.getDate() + maxAllowed - 1);
      }

      console.log(
        `  Start: ${startDateStr}, Max allowed: ${maxAllowed}, Max end: ${this.formatDateDDMMYYYY(
          maxEndDate,
        )}`,
      );

      // Get all day elements
      const dayElements = instance.calendarContainer.querySelectorAll(
        '.flatpickr-day:not(.prevMonthDay):not(.nextMonthDay)',
      );

      let markedCount = 0;

      dayElements.forEach((dayElem) => {
        const ariaLabel = dayElem.getAttribute('aria-label');
        if (!ariaLabel) return;

        const dateObj = new Date(ariaLabel);
        const dateStr = this.convertAriaLabelToDateStr(ariaLabel);

        // Check if this date is AFTER max allowed end date
        const exceedsMaxConstraint = dateObj > maxEndDate;

        if (exceedsMaxConstraint) {
          // Apply "disabled" styling
          dayElem.classList.add('flatpickr-disabled');
          dayElem.setAttribute('data-max-exceeded', 'true');
          dayElem.removeAttribute('tabindex');

          // Use capture phase to prevent clicks
          dayElem.addEventListener(
            'click',
            (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
            },
            true,
          );

          // Apply tooltip with max constraint info
          if (dayElem._tippyMax) {
            dayElem._tippyMax.destroy();
          }
          const unitLabel =
            this.product === 'Accommodation' ? 'nights' : 'days';
          dayElem._tippyMax = tippy(dayElem, {
            content: `Max ${maxAllowed} ${unitLabel}`,
            theme: 'light-border',
            trigger: 'mouseenter focus',
          });

          markedCount++;
        }
      });

      console.log(`  📊 Summary: Marked ${markedCount} dates as exceeding max`);
    },

    // Custom field help modal methods
    openCustomFieldHelpModal(customField) {
      this.selectedCustomFieldForHelp = customField;
      this.showCustomFieldHelpModal = true;
    },
  });
}
